import { useState } from 'react';
import { Button, Checkbox, Dialog, Input, PriceInput, Select, Textarea } from '@artosera/ui';
import {
  useModifierReglagesPaiement,
  useReglagesPaiement,
  type PalierPaiement,
  type ReglagesPaiement,
} from '../../api/paiementEnLigne.js';
import { Field } from '../Field.js';
import { ErreurPaiement, useTextePaiement } from './paiementUi.js';
import styles from './PaiementEnLigne.module.css';

export function Reglages({ galerieSlug }: { galerieSlug: string }) {
  const lecture = useReglagesPaiement(galerieSlug);
  const { t, label } = useTextePaiement();
  return (
    <section className={styles.section}>
      {lecture.isLoading && <p>{t('commun:chargement')}</p>}
      <ErreurPaiement erreur={lecture.error} />
      <Button
        onClick={() => {
          void lecture.refetch();
        }}
      >
        {label('rechargerReglages')}
      </Button>
      {lecture.data && (
        <FormulaireReglages
          key={lecture.data.reglages.version}
          galerieSlug={galerieSlug}
          initial={lecture.data.reglages}
        />
      )}
    </section>
  );
}
function FormulaireReglages({
  galerieSlug,
  initial,
}: {
  galerieSlug: string;
  initial: ReglagesPaiement;
}) {
  const { t, label, langue } = useTextePaiement();
  const [valeurs, setValeurs] = useState(initial);
  const [confirmer, setConfirmer] = useState(false);
  const mutation = useModifierReglagesPaiement(galerieSlug);
  const changerPalier = (index: number, ajout: Partial<PalierPaiement>) =>
    setValeurs({
      ...valeurs,
      relances: {
        ...valeurs.relances,
        paliers: valeurs.relances.paliers.map((p, i) => (i === index ? { ...p, ...ajout } : p)),
      },
    });
  async function sauver() {
    await mutation.mutateAsync({
      versionAttendue: initial.version,
      acceptePaiements: valeurs.acceptePaiements,
      paiementPartiel: valeurs.paiementPartiel,
      dureeLienJours: valeurs.dureeLienJours,
      relances: valeurs.relances,
    });
    setConfirmer(false);
  }
  return (
    <form
      className={styles.formulaire}
      onSubmit={(e) => {
        e.preventDefault();
        if (valeurs.relances.mode === 'automatique' && initial.relances.mode !== 'automatique')
          setConfirmer(true);
        else void sauver().catch(() => {});
      }}
    >
      <h2>{label('reglages')}</h2>
      <p>
        {label('version')} : {initial.version}
      </p>
      <Checkbox
        label={label('acceptePaiements')}
        checked={valeurs.acceptePaiements}
        onCheckedChange={(checked) =>
          setValeurs({ ...valeurs, acceptePaiements: checked === true })
        }
      />
      <Checkbox
        label={label('partiel')}
        checked={valeurs.paiementPartiel.autorise}
        onCheckedChange={(checked) =>
          setValeurs({
            ...valeurs,
            paiementPartiel: { ...valeurs.paiementPartiel, autorise: checked === true },
          })
        }
      />
      <Field label={label('minimum')} htmlFor="paiement-minimum">
        <PriceInput
          id="paiement-minimum"
          lang={langue}
          cents={valeurs.paiementPartiel.minimumCentimes}
          onCentsChange={(minimumCentimes) =>
            setValeurs({
              ...valeurs,
              paiementPartiel: { ...valeurs.paiementPartiel, minimumCentimes },
            })
          }
        />
      </Field>
      <Field label={label('duree')} htmlFor="paiement-duree">
        <Input
          id="paiement-duree"
          type="number"
          required
          min={1}
          max={365}
          value={valeurs.dureeLienJours}
          onChange={(e) => setValeurs({ ...valeurs, dureeLienJours: Number(e.target.value) })}
        />
      </Field>
      <Field label={label('mode')} htmlFor="paiement-mode">
        <Select
          id="paiement-mode"
          value={valeurs.relances.mode}
          onValueChange={(mode) =>
            setValeurs({
              ...valeurs,
              relances: { ...valeurs.relances, mode: mode as 'proposer' | 'automatique' },
            })
          }
          options={['proposer', 'automatique'].map((v) => ({ value: v, label: label(v) }))}
        />
      </Field>
      {valeurs.relances.paliers.map((p, index) => (
        <fieldset key={p.rang} className={styles.formulaire}>
          <legend>
            {label('palier')} {p.rang}
          </legend>
          <Checkbox
            label={label('actif')}
            checked={p.actif}
            onCheckedChange={(checked) => changerPalier(index, { actif: checked === true })}
          />
          <Field label={label('delai')} htmlFor={`delai-${p.rang}`}>
            <Input
              id={`delai-${p.rang}`}
              type="number"
              required
              min={-30}
              max={365}
              value={p.delaiJours}
              onChange={(e) => changerPalier(index, { delaiJours: Number(e.target.value) })}
            />
          </Field>
          <Field label={label('ton')} htmlFor={`ton-${p.rang}`}>
            <Select
              id={`ton-${p.rang}`}
              value={p.ton}
              onValueChange={(v) => changerPalier(index, { ton: v as PalierPaiement['ton'] })}
              options={['courtois', 'ferme', 'dernier_rappel'].map((v) => ({
                value: v,
                label: label(`tons.${v}`),
              }))}
            />
          </Field>
          {(['objet', 'message'] as const).flatMap((champ) =>
            (['fr', 'en'] as const).map((lang) => (
              <Field
                key={`${champ}-${lang}`}
                label={`${label(champ)} ${lang.toUpperCase()}`}
                htmlFor={`${champ}-${lang}-${p.rang}`}
              >
                <Textarea
                  id={`${champ}-${lang}-${p.rang}`}
                  maxLength={4000}
                  required={lang === 'fr' && !!p[champ]?.en}
                  value={p[champ]?.[lang] ?? ''}
                  onChange={(e) => {
                    const texte = {
                      fr: p[champ]?.fr ?? '',
                      en: p[champ]?.en ?? '',
                      [lang]: e.target.value,
                    };
                    const palier = { ...p };
                    palier[champ] = texte.fr || texte.en ? texte : undefined;
                    changerPalier(index, palier);
                  }}
                />
              </Field>
            )),
          )}
        </fieldset>
      ))}
      <ErreurPaiement erreur={mutation.error} />
      <Button
        type="submit"
        loading={mutation.isPending}
        disabled={valeurs.paiementPartiel.minimumCentimes <= 0}
      >
        {label('enregistrer')}
      </Button>
      <Dialog
        open={confirmer}
        onOpenChange={setConfirmer}
        title={label('confirmerAutomatique')}
        closeLabel={t('commun:fermer')}
      >
        <p>{label('automatiqueExplication')}</p>
        <ErreurPaiement erreur={mutation.error} />
        <Button
          loading={mutation.isPending}
          onClick={() => {
            void sauver().catch(() => {});
          }}
        >
          {label('confirmer')}
        </Button>
      </Dialog>
    </form>
  );
}
